import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import { toast } from "sonner";
import {
  Check, Star, Clock, ShieldCheck, Truck, BadgeCheck, ChevronRight,
  Zap, RefreshCw, MessageCircle, Award, ArrowRight, ChevronLeft,
  Share2, Plus, Minus, RotateCw, Video as VideoIcon
} from "lucide-react";
import Pano360Viewer from "../components/Pano360Viewer";
import YouTubeEmbed from "../components/YouTubeEmbed";
import useDocumentTitle from "../lib/useDocumentTitle";

const PACKAGE_IMAGES = {
  basic: [
    "https://housedesignwala.com/api/files/5d05ac48-5ce6-41c1-9f55-804a85afa0e6",
    "https://images.unsplash.com/photo-1742415106102-77bbfe14b872?w=1200",
    "https://images.unsplash.com/photo-1503594384566-461fe158e797?w=1200",
    "https://images.unsplash.com/photo-1503387837-b154d5074bd2?w=1200",
    "https://images.unsplash.com/photo-1627141234469-24711efb373c?w=1200",
  ],
  // NOTE: the Basic package's first image ("5d05ac48-...") is a real,
  // brand-correct asset for Basic — a promo graphic that literally reads
  // "BASIC PACKAGE" on it. It was previously reused here for Premium and
  // Luxury too, which showed the wrong package's own branding on both
  // pages. Removed rather than reused; still just generic stock photos
  // until real Premium/Luxury sample photos are uploaded via
  // /admin/services (see the watermark/caption note on `usingCms` below).
  premium: [
    "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?w=1200",
    "https://images.unsplash.com/photo-1742415106102-77bbfe14b872?w=1200",
    "https://images.unsplash.com/photo-1668642538782-9579e75b0f4b?w=1200",
    "https://images.pexels.com/photos/5563473/pexels-photo-5563473.jpeg?w=1200",
  ],
  luxury: [
    "https://images.unsplash.com/photo-1668642538782-9579e75b0f4b?w=1200",
    "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?w=1200",
    "https://images.pexels.com/photos/5563473/pexels-photo-5563473.jpeg?w=1200",
    "https://images.unsplash.com/photo-1627141234469-24711efb373c?w=1200",
  ],
};

const FAQS = [
  { q: "Are these drawings approval-ready?", a: "Our drawings are construction-ready. For municipal/local body approval, a registered structural engineer in your city must stamp them. We can recommend one." },
  { q: "Do you handle G+3 / G+4 / commercial buildings?", a: "Luxury package supports up to G+3. For taller/commercial buildings, contact us on WhatsApp for a custom quote." },
  { q: "What software / file formats do I receive?", a: "AutoCAD (.dwg + PDF) for 2D drawings. SketchUp/3ds Max/Lumion renders for 3D as JPEG/PNG. Source files on request." },
  { q: "How many revisions are included?", a: "Basic: 2 revisions, Premium: 3 revisions, Luxury: Unlimited 2D + 2 for 3D. Extra revisions can be purchased." },
  { q: "What if I'm not satisfied?", a: "We have a 100% revision policy until you're happy. If, after all included revisions, you're still unhappy, we offer a partial refund based on work completed." },
];

function Stars({ rating, size = "w-4 h-4" }) {
  return (
    <div className="flex">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`${size} ${i <= rating ? "fill-[#F7941D] text-[#F7941D]" : "text-gray-300"}`} />
      ))}
    </div>
  );
}

// Diagonal "SAMPLE DRAWING" stamp — only ever rendered over an admin-
// uploaded gallery image (see `usingCms` below), never over the emergency
// hardcoded stock-photo fallback, since those aren't drawings at all and
// stamping them "sample drawing" would misrepresent them to a client.
function SampleStamp({ text }) {
  if (!text) return null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden select-none">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[28deg] whitespace-nowrap">
        <span className="block px-8 py-1.5 bg-[#1a3c5e]/80 text-white font-display font-extrabold uppercase tracking-[0.15em] text-xs sm:text-sm shadow-lg border-y-2 border-[#F7941D]/70">
          {text}
        </span>
      </div>
    </div>
  );
}

function RatingBar({ value, total, label }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-3 text-right">{label}</span>
      <Star className="w-3 h-3 fill-[#F7941D] text-[#F7941D]" />
      <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full bg-[#F7941D]" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right text-gray-600">{value}</span>
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [reviewSort, setReviewSort] = useState("recent");
  const [reviewFilter, setReviewFilter] = useState(0); // 0 = all
  const [reviewLimit, setReviewLimit] = useState(6);

  useDocumentTitle(
    s ? `${s.name} Package — ${s.subtitle}` : null,
    s ? `${s.name} package: ${s.subtitle}. ₹${s.price} · ${s.delivery_text}. Order construction drawings online, delivered pan-India.` : null
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/services/${slug}`),
      api.get(`/reviews/service/${slug}`),
    ]).then(([sr, rr]) => {
      if (cancelled) return;
      setS(sr.data);
      setReviews(rr.data);
      setActiveImg(0);
      setReviewLimit(6);
    });
    return () => { cancelled = true; };
  }, [slug]);

  // The watermark + per-image caption only ever apply to a real
  // admin-uploaded gallery (`media_images`) — never to the hardcoded
  // fallback below, which exists purely so the page never shows a blank
  // gallery and isn't guaranteed to actually be photos of drawings.
  const usingCms = !!(s?.media_images && s.media_images.length > 0);
  const images = usingCms ? s.media_images : (PACKAGE_IMAGES[slug] || PACKAGE_IMAGES.basic);
  const captions = usingCms ? (s.media_captions || []) : [];
  const sampleLabel = usingCms ? (s.sample_label || "") : "";
  const media360 = s?.media_360 || [];
  const mediaVideos = s?.media_videos || [];

  const ratingStats = useMemo(() => {
    if (reviews.length === 0) return { avg: 0, total: 0, dist: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }, pct5: 0 };
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => { dist[r.rating] = (dist[r.rating] || 0) + 1; });
    const avg = reviews.reduce((a, b) => a + b.rating, 0) / reviews.length;
    return { avg, total: reviews.length, dist, pct5: Math.round((dist[5] + dist[4]) / reviews.length * 100) };
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    let arr = reviews;
    if (reviewFilter > 0) arr = arr.filter(r => r.rating === reviewFilter);
    if (reviewSort === "rating-high") arr = [...arr].sort((a, b) => b.rating - a.rating);
    else if (reviewSort === "rating-low") arr = [...arr].sort((a, b) => a.rating - b.rating);
    else arr = [...arr].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return arr;
  }, [reviews, reviewSort, reviewFilter]);

  const sharePackage = async () => {
    const shareData = { title: s?.name, text: `Check out ${s?.name} on HouseDesignWala`, url: window.location.href };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  if (!s) return <div className="py-20 text-center text-gray-500">Loading...</div>;

  const discount = s.original_price > s.price ? Math.round((1 - s.price / s.original_price) * 100) : 0;
  const otherPackages = [
    { slug: "basic", name: "Basic", price: 8500 },
    { slug: "premium", name: "Premium", price: 13500 },
    { slug: "luxury", name: "Luxury", price: 20500 },
  ].filter(p => p.slug !== slug);

  return (
    <div className="bg-[#F1F3F6]" data-testid="service-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
          <nav className="text-xs text-gray-500 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#F7941D]">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-[#F7941D]">Construction Drawings</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1a3c5e] font-semibold">{s.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 grid lg:grid-cols-12 gap-4">
        {/* Left: Image gallery (sticky on desktop) */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-20 bg-white rounded-lg p-4 shadow-sm">
            <div className="flex gap-3">
              <div className="hidden sm:flex flex-col gap-2 w-16">
                {images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    onMouseEnter={() => setActiveImg(i)}
                    data-testid={`thumb-${i}`}
                    className={`aspect-square rounded border-2 overflow-hidden transition relative ${activeImg === i ? "border-[#F7941D]" : "border-gray-200 hover:border-[#1a3c5e]/30"}`}
                  >
                    <img src={src} alt={captions[i] || ""} className="w-full h-full object-cover" />
                    {sampleLabel && <div className="absolute inset-x-0 bottom-0 bg-[#1a3c5e]/70 text-white text-[6px] font-bold text-center leading-tight py-0.5">SAMPLE</div>}
                  </button>
                ))}
              </div>
              <div className="flex-1 relative">
                <div className="aspect-square sm:aspect-[4/5] bg-white rounded-lg overflow-hidden border border-gray-200 relative" data-testid="main-image">
                  <img src={images[activeImg]} alt={captions[activeImg] || s.name} className="w-full h-full object-cover" />
                  <SampleStamp text={sampleLabel} />
                  {captions[activeImg] && (
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-4 pt-8 pb-3">
                      <span className="text-white text-sm font-semibold drop-shadow" data-testid="image-caption">{captions[activeImg]}</span>
                    </div>
                  )}
                </div>
                <button onClick={() => setActiveImg((activeImg - 1 + images.length) % images.length)} className="sm:hidden absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => setActiveImg((activeImg + 1) % images.length)} className="sm:hidden absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center">
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="sm:hidden absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {images.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${activeImg === i ? "bg-[#F7941D]" : "bg-white/70"}`} />)}
                </div>
                <div className="absolute top-3 right-3 flex flex-col gap-2">
                  <button onClick={sharePackage} className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center hover:text-[#1a3c5e]" data-testid="share-btn"><Share2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <Link to={`/checkout/${s.slug}`} data-testid="gallery-buy-btn">
                <button className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-lg shadow-md flex items-center justify-center gap-2">
                  <Zap className="w-4 h-4" /> BUY NOW
                </button>
              </Link>
              <a href="https://wa.me/919050785528" target="_blank" rel="noopener noreferrer">
                <button className="w-full py-3 bg-[#1a3c5e] hover:bg-[#122a42] text-white font-bold rounded-lg shadow-md flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" /> ENQUIRE
                </button>
              </a>
            </div>
          </div>
        </div>

        {/* Right: details */}
        <div className="lg:col-span-7 space-y-3">
          {/* Title block */}
          <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm">
            <div className="text-xs text-[#F7941D] font-semibold uppercase tracking-wider">HouseDesignWala · {s.slug.toUpperCase()}</div>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-[#1a3c5e] mt-1" data-testid="product-title">{s.name} — {s.subtitle}</h1>

            <div className="flex items-center gap-3 mt-2">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600 text-white text-xs font-bold">
                {ratingStats.avg.toFixed(1)} <Star className="w-3 h-3 fill-current" />
              </div>
              <span className="text-xs text-gray-500 font-medium">{ratingStats.total} Ratings & Reviews</span>
              <span className="text-xs text-emerald-700 font-semibold">Assured</span>
            </div>

            <div className="mt-4 flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-3xl sm:text-4xl font-extrabold text-[#1a3c5e]" data-testid="product-price">{inr(s.price)}</span>
              {s.original_price > s.price && (
                <>
                  <span className="text-base text-gray-400 line-through">{inr(s.original_price)}</span>
                  <span className="text-base text-emerald-600 font-bold">{discount}% off</span>
                </>
              )}
            </div>
            <div className="text-xs text-emerald-700 font-semibold mt-1">+ {inr(0)} delivery · 100% online, PAN-India</div>

            {/* Offers */}
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">Available Offers</div>
              <ul className="mt-2 space-y-1.5 text-sm">
                <li className="flex gap-2"><BadgeCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span><b>Bank Offer</b> 10% Off on First Order · Use code <b className="text-[#F7941D]">FIRST10</b></span></li>
                <li className="flex gap-2"><BadgeCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span><b>Bundle Offer</b> Book 2 Packages — Get 15% Off · Code <b className="text-[#F7941D]">BUNDLE15</b></span></li>
                <li className="flex gap-2"><BadgeCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span><b>EMI Offer</b> Starting {inr(s.emi_per_month)}/month via Snapmint · No-cost option available</span></li>
                <li className="flex gap-2"><BadgeCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span><b>Free Consultation</b> 15-min architect call on WhatsApp before order</span></li>
              </ul>
            </div>

            {/* Delivery */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] mb-2">Delivery</div>
              <div className="text-xs text-emerald-700 font-semibold" data-testid="delivery-info">
                ✓ Available anywhere in India · Delivered digitally, no physical shipping needed
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
                <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-[#1a3c5e]" /> 100% Online · Pan India</span>
                <span className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 text-[#1a3c5e]" /> {s.revisions} Revisions Included</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#1a3c5e]" /> {s.delivery_text}</span>
              </div>
            </div>

            {/* Other packages */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] mb-2">Compare Packages</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="border-2 border-[#F7941D] rounded-lg p-2.5 text-center bg-[#FEF3E2]/40">
                  <div className="text-[10px] font-bold uppercase text-[#F7941D]">Selected</div>
                  <div className="font-semibold text-[#1a3c5e] text-sm">{s.slug.charAt(0).toUpperCase() + s.slug.slice(1)}</div>
                  <div className="text-xs font-bold text-[#F7941D]">{inr(s.price)}</div>
                </div>
                {otherPackages.map(p => (
                  <Link key={p.slug} to={`/services/${p.slug}`} data-testid={`compare-${p.slug}`}>
                    <div className="border border-gray-200 rounded-lg p-2.5 text-center hover:border-[#1a3c5e] transition">
                      <div className="text-[10px] font-bold uppercase text-gray-500">View</div>
                      <div className="font-semibold text-[#1a3c5e] text-sm">{p.name}</div>
                      <div className="text-xs font-bold text-[#1a3c5e]">{inr(p.price)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Highlights */}
          <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm">
            <h2 className="font-display font-bold text-[#1a3c5e] mb-3">Highlights</h2>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2 text-gray-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Floor support: <b className="ml-1">{s.floor_tag}</b></li>
              <li className="flex gap-2 text-gray-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Delivery in: <b className="ml-1">{s.delivery_text}</b></li>
              <li className="flex gap-2 text-gray-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Revisions: <b className="ml-1">{s.revisions}</b></li>
              <li className="flex gap-2 text-gray-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Secure Razorpay payment · 100% Online</li>
              <li className="flex gap-2 text-gray-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> EMI starting {inr(s.emi_per_month)}/mo via Snapmint</li>
            </ul>
          </div>

          {/* 360° Panoramas — only render if admin has uploaded any */}
          {media360.length > 0 && (
            <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm" data-testid="pano360-section">
              <div className="flex items-center gap-2 mb-3">
                <RotateCw className="w-5 h-5 text-[#F7941D]" />
                <h2 className="font-display font-bold text-[#1a3c5e]">Step Inside — 360° View</h2>
              </div>
              <p className="text-xs text-gray-500 mb-3">Drag, pinch and explore. Works on mobile too.</p>
              <div className="space-y-3">
                {media360.map((src, i) => (
                  <Pano360Viewer
                    key={i}
                    src={src}
                    testId={`pano-${i}`}
                    className="w-full h-[320px] sm:h-[420px]"
                  />
                ))}
              </div>
            </div>
          )}

          {/* YouTube Videos — only render if admin has added any */}
          {mediaVideos.length > 0 && (
            <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm" data-testid="videos-section">
              <div className="flex items-center gap-2 mb-3">
                <VideoIcon className="w-5 h-5 text-[#D32F2F]" />
                <h2 className="font-display font-bold text-[#1a3c5e]">Walkthrough Videos</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {mediaVideos.map((url, i) => (
                  <YouTubeEmbed key={i} url={url} title={`${s.name} video ${i + 1}`} testId={`yt-${i}`} />
                ))}
              </div>
            </div>
          )}

          {/* What you get */}
          <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm">
            <h2 className="font-display font-bold text-[#1a3c5e] mb-3">What&apos;s Included</h2>
            <ul className="grid sm:grid-cols-2 gap-2.5">
              {s.includes_list.map((x, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Specifications */}
          <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm">
            <h2 className="font-display font-bold text-[#1a3c5e] mb-3">Specifications</h2>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-200">
                <SpecRow label="Package Type" value={s.subtitle} />
                <SpecRow label="Floors Supported" value={s.floor_tag} />
                <SpecRow label="Delivery Time" value={s.delivery_text} />
                <SpecRow label="Revisions Included" value={s.revisions} />
                <SpecRow label="Output Format" value="AutoCAD (.dwg) + PDF + 3D Renders (JPG/PNG)" />
                <SpecRow label="Mode of Service" value="100% Online · Pan India" />
                <SpecRow label="Payment Options" value="UPI / Card / NetBanking / Snapmint EMI" />
                <SpecRow label="Pre-Order Consultation" value="Free 15-min architect call on WhatsApp" />
              </tbody>
            </table>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm">
            <h2 className="font-display font-bold text-[#1a3c5e] mb-3">Questions and Answers</h2>
            <div className="space-y-2">
              {FAQS.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} idx={i} />)}
            </div>
          </div>

          {/* Trust strip */}
          <div className="bg-white rounded-lg p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 shadow-sm">
            {[
              { i: <Award className="w-5 h-5" />, t: "500+ Happy Homes" },
              { i: <ShieldCheck className="w-5 h-5" />, t: "Secure Payment" },
              { i: <RefreshCw className="w-5 h-5" />, t: "Revisions Included" },
              { i: <MessageCircle className="w-5 h-5" />, t: "WhatsApp Support" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-[#1a3c5e]">
                <div className="w-9 h-9 rounded-full bg-[#FEF3E2] text-[#F7941D] flex items-center justify-center">{b.i}</div>
                <div className="font-semibold">{b.t}</div>
              </div>
            ))}
          </div>

          {/* Ratings & Reviews — moved to bottom */}
          <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm" data-testid="reviews-section">
            <h2 className="font-display font-bold text-[#1a3c5e] mb-4">Ratings & Reviews</h2>

            <div className="grid sm:grid-cols-2 gap-6">
              {/* Aggregate */}
              <div>
                <div className="flex items-baseline gap-3">
                  <div className="font-display text-5xl font-extrabold text-[#1a3c5e]">{ratingStats.avg.toFixed(1)}</div>
                  <Star className="w-5 h-5 fill-[#F7941D] text-[#F7941D]" />
                </div>
                <div className="text-sm text-gray-500 mt-1">{ratingStats.total} Ratings & Reviews</div>
                {ratingStats.total > 0 && (
                  <div className="mt-1 text-xs text-emerald-700 font-semibold">{ratingStats.pct5}% recommend this package</div>
                )}
                <div className="mt-4 space-y-1.5">
                  {[5,4,3,2,1].map(n => <RatingBar key={n} label={n} value={ratingStats.dist[n] || 0} total={ratingStats.total} />)}
                </div>
              </div>

              {/* Sort + filter */}
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] mb-2">Sort by</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: "recent", label: "Most Recent" },
                      { id: "rating-high", label: "Highest Rated" },
                      { id: "rating-low", label: "Lowest Rated" },
                    ].map(o => (
                      <button
                        key={o.id}
                        onClick={() => setReviewSort(o.id)}
                        data-testid={`review-sort-${o.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                          reviewSort === o.id ? "bg-[#1a3c5e] text-white" : "bg-gray-100 text-[#1a3c5e] hover:bg-gray-200"
                        }`}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] mb-2">Filter</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setReviewFilter(0)}
                      data-testid="review-filter-all"
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold ${reviewFilter === 0 ? "bg-[#F7941D] text-white" : "bg-gray-100 text-[#1a3c5e]"}`}
                    >All</button>
                    {[5,4,3,2,1].map(n => (
                      <button
                        key={n}
                        onClick={() => setReviewFilter(n)}
                        data-testid={`review-filter-${n}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 ${reviewFilter === n ? "bg-[#F7941D] text-white" : "bg-gray-100 text-[#1a3c5e]"}`}
                      >{n} <Star className="w-3 h-3 fill-current" /></button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 divide-y divide-gray-200">
              {filteredReviews.slice(0, reviewLimit).map(r => (
                <div key={r.id} className="py-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold text-white ${r.rating >= 4 ? "bg-emerald-600" : r.rating >= 3 ? "bg-amber-500" : "bg-red-500"}`}>
                      {r.rating} <Star className="w-2.5 h-2.5 fill-current" />
                    </span>
                    <span className="text-xs text-gray-500">·</span>
                    <span className="text-xs text-gray-700 font-semibold">Verified Customer</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{r.review_text}</p>
                  <div className="text-xs text-gray-500 mt-2 flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#1a3c5e]">{r.reviewer_name}</span>
                    <span>·</span>
                    <span>{r.city}</span>
                    <span>·</span>
                    <span>{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span className="ml-auto text-emerald-700 flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Certified Buyer</span>
                  </div>
                </div>
              ))}
              {filteredReviews.length === 0 && <p className="text-sm text-gray-500 py-4">No reviews match this filter.</p>}
            </div>

            {filteredReviews.length > reviewLimit && (
              <button
                onClick={() => setReviewLimit(reviewLimit + 8)}
                data-testid="load-more-reviews"
                className="mt-3 w-full py-2.5 text-sm font-bold text-[#F7941D] hover:bg-[#FEF3E2]/40 rounded-lg border-t border-gray-200"
              >
                Show More Reviews ({filteredReviews.length - reviewLimit} more) →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sticky mobile buy bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 grid grid-cols-2 gap-2 p-2 shadow-lg">
        <a href="https://wa.me/919050785528" target="_blank" rel="noopener noreferrer">
          <button className="w-full py-3 bg-[#1a3c5e] text-white font-bold rounded-lg flex items-center justify-center gap-2 text-sm">
            <MessageCircle className="w-4 h-4" /> CHAT
          </button>
        </a>
        <Link to={`/checkout/${s.slug}`}>
          <button className="w-full py-3 bg-[#F7941D] text-white font-bold rounded-lg flex items-center justify-center gap-2 text-sm">
            <Zap className="w-4 h-4" /> BUY {inr(s.price)}
          </button>
        </Link>
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <tr>
      <td className="py-2.5 text-gray-500 w-1/2 sm:w-1/3 align-top">{label}</td>
      <td className="py-2.5 text-[#1a3c5e] font-medium">{value}</td>
    </tr>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-${idx}`}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#FEF3E2]/40"
      >
        <span className="font-semibold text-[#1a3c5e] text-sm">{q}</span>
        {open ? <Minus className="w-4 h-4 text-[#F7941D]" /> : <Plus className="w-4 h-4 text-[#F7941D]" />}
      </button>
      {open && <div className="px-4 pb-3 text-sm text-gray-600">{a}</div>}
    </div>
  );
}
